import * as React from "react";
import { CalendarDays } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { formatDisplay, parseDisplay } from "@/lib/datetime-format";

export interface DateTimeFieldProps {
  value: string | null;
  onChange: (value: string | null) => void;
  id?: string;
  label?: string;
  minIso?: string;
  maxIso?: string;
  invalid?: boolean;
  emptyHint?: string;
  disabled?: boolean;
}

function maskDateTime(input: string): string {
  const d = input.replace(/\D/g, "").slice(0, 12);
  let out = d.slice(0, 2);
  if (d.length > 2) out += "/" + d.slice(2, 4);
  if (d.length > 4) out += "/" + d.slice(4, 8);
  if (d.length > 8) out += " " + d.slice(8, 10);
  if (d.length > 10) out += ":" + d.slice(10, 12);
  return out;
}

function timeString(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function DateTimeField({
  value,
  onChange,
  id,
  label,
  minIso,
  maxIso,
  invalid,
  emptyHint,
  disabled,
}: DateTimeFieldProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(() => formatDisplay(value));
  const [seenValue, setSeenValue] = React.useState(value);
  if (value !== seenValue) {
    setSeenValue(value);
    setText(formatDisplay(value));
  }

  const parsed = parseDisplay(text);
  const localInvalid = text.trim() !== "" && !parsed.valid;
  const showInvalid = Boolean(invalid) || localInvalid;
  const isEmpty = text.trim() === "";

  const current = parsed.iso ? new Date(parsed.iso) : value ? new Date(value) : undefined;
  const minDate = minIso ? new Date(minIso) : undefined;
  const maxDate = maxIso ? new Date(maxIso) : undefined;

  function emit(iso: string | null) {
    setText(formatDisplay(iso));
    onChange(iso);
  }

  function handleType(e: React.ChangeEvent<HTMLInputElement>) {
    const masked = maskDateTime(e.target.value);
    setText(masked);
    const { iso, valid } = parseDisplay(masked);
    if (valid) onChange(iso);
  }

  function handleDay(date: Date | undefined) {
    if (!date) return;
    const base = current ?? new Date();
    emit(
      new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        base.getHours(),
        base.getMinutes()
      ).toISOString()
    );
  }

  function handleTime(e: React.ChangeEvent<HTMLInputElement>) {
    const [hh, mm] = e.target.value.split(":").map((n) => Number(n) || 0);
    const base = current ?? new Date();
    emit(new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh, mm).toISOString());
  }

  return (
    <div className="space-y-1">
      {label ? (
        <Label htmlFor={id} className="text-[11px]">
          {label}
        </Label>
      ) : null}
      <div className="flex items-center gap-2">
        <Input
          id={id}
          value={text}
          onChange={handleType}
          placeholder="dd/mm/aaaa hh:mm"
          inputMode="numeric"
          disabled={disabled}
          aria-invalid={showInvalid}
          className={cn(showInvalid && "border-destructive focus-visible:ring-destructive")}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Abrir calendário"
              className="hover:bg-bg-muted inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
            >
              <CalendarDays className="h-4 w-4 text-text-secondary" />
            </button>
          </PopoverTrigger>
          <PopoverContent>
            <Calendar
              mode="single"
              selected={current}
              onSelect={handleDay}
              defaultMonth={current}
              disabled={
                minDate || maxDate ? { before: minDate as Date, after: maxDate as Date } : undefined
              }
            />
            <div className="mt-2 flex items-center gap-2 border-t pt-2">
              <Label htmlFor={`${id}-time`} className="text-[11px] text-text-secondary">
                Hora
              </Label>
              <Input
                id={`${id}-time`}
                type="time"
                value={current ? timeString(current) : ""}
                onChange={handleTime}
                className="h-8 w-28"
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {}
      {localInvalid ? (
        <p className="text-xs text-destructive" role="alert">
          Data/hora inválida. Use o formato dd/mm/aaaa hh:mm.
        </p>
      ) : isEmpty && emptyHint ? (
        <p className="text-[11px] text-text-tertiary">{emptyHint}</p>
      ) : null}
    </div>
  );
}
